import { isEqual } from 'lodash';
import { Capacitor } from '@capacitor/core';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { differenceInSeconds, parseISO } from 'date-fns';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';

import { LoyaltySettingsType } from '@sifca-monorepo/widget-generator';

import { MobileAuthService } from '../../services';
import { reputationPicture } from '../../environments/environment';
import { MobileProfileService } from '../../services/profile.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { MobileReputationService } from '../../services/reputation.service';
declare const google: any;

@Component({
  template: '',
})
export class MobileLoginComponent {
  protected unsubscribeAll: Subject<void> = new Subject<void>();

  userId: string;
  codeClient: any;
  authForm: FormGroup;
  isConfirmHidden = true;
  isButtonDisabled = true;
  selectedOption = 'email';
  settings: LoyaltySettingsType;
  reputationPicture = reputationPicture;
  widgetSettings$ = this.reputationService.widgetSettings$;
  clientId: string = '315954860908-89q6o22d7bm9p2lhpim6s2sqtatvfo3b.apps.googleusercontent.com';

  constructor(
    protected cd: ChangeDetectorRef,
    protected navCtrl: NavController,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected toasterService: ToasterService,
    protected authService: MobileAuthService,
    protected profileService: MobileProfileService,
    protected reputationService: MobileReputationService,
  ) {
    this.authForm = this.formBuilder.group({
      email: ['', Validators.email],
      phone: this.formBuilder.group({
        number: ['', Validators.pattern(/^\d{8}$/)],
        countryCode: ['216'],
      }),
      pwd: this.formBuilder.group({
        password: [''],
      }),
    });
    const initialValues = this.authForm.value;
    this.authForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
      this.isButtonDisabled = isEqual(values, initialValues);
      this.cd.markForCheck();
    });
    this.reputationService.loyaltySettings$.pipe(takeUntil(this.unsubscribeAll)).subscribe((loyaltySettings) => {
      this.settings = loyaltySettings;
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.loadGoogleSDK();
    }, 2000);
  }

  loginWithApple(): void {
    this.authService
      .appleLogin()
      .then((response) => {
        const authorizationData = {
          authorizationCode: response.authorizationCode,
          userIdentifier: response.user,
          identityToken: response.identityToken,
          familyName: response.familyName,
          givenName: response.givenName,
          email: response.email,
          state: response.state,
        };
        this.authService
          .loginWithAppleForTarget(authorizationData)
          .pipe(
            switchMap((res) => {
              this.userId = res.user?.id;
              const isRegister = !!res.user.createdAt && differenceInSeconds(new Date(), parseISO(res.user.createdAt)) < 60;
              return this.handleLoginResponse(res.accessToken, isRegister);
            }),
          )
          .subscribe(() => {
            this.navCtrl.navigateRoot('/');
          });
      })
      .catch();
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onChangeOption(option: string) {
    this.selectedOption = option;
    this.clearAllValidators(option);
    this.cd.markForCheck();
  }

  clearAllValidators(option: string) {
    if (option === 'email') {
      const phoneControl = this.authForm.get('phone.number');
      phoneControl?.clearValidators();
      phoneControl?.updateValueAndValidity();
      const emailControl = this.authForm.get('email');
      emailControl?.setValidators([Validators.required, Validators.email]);
      emailControl?.updateValueAndValidity();
    } else if (option === 'phone') {
      const emailControl = this.authForm.get('email');
      emailControl?.clearValidators();
      emailControl?.updateValueAndValidity();
      const phoneControl = this.authForm.get('phone.number');
      phoneControl?.setValidators([Validators.required, Validators.pattern(/^\d{8}$/)]); // Example pattern for numbers
      phoneControl?.updateValueAndValidity();
    }
  }

  login() {
    this.isButtonDisabled = true;
    const input = {
      ...(this.selectedOption === 'email' ? { login: this.authForm.get('email').value } : {}),
      ...(this.selectedOption === 'phone'
        ? {
            phone: {
              countryCode: this.authForm.get(['phone', 'countryCode']).value,
              number: this.authForm.get(['phone', 'number']).value.toString(),
            },
          }
        : {}),
      password: this.authForm.get(['pwd', 'password']).value,
    };
    this.authService
      .loginForTarget(input)
      .pipe(
        catchError((error) => {
          let text: string;
          if (error && error.includes('No user with this login or phone')) {
            text = this.translate.instant('NO_USER_WITH_THIS_LOGIN');
          } else {
            text = this.translate.instant('STH_WENT_WRONG');
          }
          this.toasterService.error(text);
          this.cd.markForCheck();
          return of(null);
        }),
        switchMap((res) => {
          if (Capacitor.getPlatform() !== 'web') {
            this.authService.addFcmToken();
          }
          if (!res) {
            return of(null);
          }
          this.userId = res.user?.id;
          return this.handleLoginResponse(res.accessToken);
        }),
      )
      .subscribe(() => {
        this.navCtrl.navigateRoot('/');
        this.cd.markForCheck();
      });
  }

  loadGoogleSDK() {
    setTimeout(() => {
      this.initializeOAuthClient();
    }, 1000);
  }

  initializeOAuthClient() {
    this.codeClient = google?.accounts.oauth2.initTokenClient({
      client_id: this.clientId,
      scope: 'openid email profile',
      callback: (response: any) => {
        this.authService
          .loginWithGoogleForTarget(response.access_token)
          .pipe(
            switchMap((res) => {
              this.userId = res.user?.id;
              const isRegister = !!res.user.createdAt && differenceInSeconds(new Date(), parseISO(res.user.createdAt)) < 60;
              return this.handleLoginResponse(res.accessToken, isRegister);
            }),
          )
          .subscribe(() => {
            this.navCtrl.navigateRoot('/');
            this.cd.markForCheck();
          });
      },
    });
  }

  loginWithGoogle() {
    this.codeClient.requestAccessToken();
  }

  loginWithFacebook(): void {
    this.authService.facebookLogin().then((response) => {
      this.authService
        .loginWithFacebookForTarget(response.accessToken)
        .pipe(
          switchMap((res) => {
            if (Capacitor.getPlatform() !== 'web') {
              this.authService.addFcmToken();
            }
            return of(res);
          }),
          switchMap((res) => {
            this.userId = res.user?.id;
            const isRegister = !!res.user.createdAt && differenceInSeconds(new Date(), parseISO(res.user.createdAt)) < 60;
            return this.handleLoginResponse(res.accessToken, isRegister);
          }),
        )
        .subscribe(() => {
          this.navCtrl.navigateRoot('/');
          this.cd.markForCheck();
        });
    });
  }

  handleLoginResponse(accessToken: string, isRegister: boolean = false): Observable<any> {
    this.authService.authenticated$ = true;
    localStorage.setItem('elvkwdigttoken', accessToken);
    localStorage.setItem('elevkusr', this.userId);
    this.reputationService.isFinalLevel$ = false;

    if (isRegister) {
      if (this.settings?.welcome?.active && this.settings?.welcome?.remuneration) {
        const remuneration = this.settings?.welcome?.remuneration;

        const qualitativePic = this.settings?.qualitative?.picture;
        const qualitativeIconUrl =
          qualitativePic?.baseUrl && qualitativePic?.path ? `${qualitativePic.baseUrl}/${qualitativePic.path}` : this.reputationPicture;

        const quantitativePic = remuneration?.quantitative?.wallet?.coin?.picture;
        const quantitativeIconUrl = quantitativePic?.baseUrl && quantitativePic?.path ? `${quantitativePic.baseUrl}/${quantitativePic.path}` : null;

        this.toasterService.welcome(
          {
            qualitativeAmount: remuneration.qualitative?.amount,
            qualitativeIconUrl: qualitativeIconUrl,
            quantitativeAmount: remuneration.quantitative?.amount,
            quantitativeIconUrl: quantitativeIconUrl,
          },
          this.translate.instant('WELCOME_BONUS_TITLE'),
          this.translate.instant('WELCOME_BONUS_DESC'),
          9000,
        );
      }
    }
    return this.authService.getWidgetUserForTarget();
  }

  ngOnDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }
}
